import { useEffect, useState } from "react";
import { Button, Form, InputGroup, Offcanvas, Row, Col } from "react-bootstrap";
import { PersonPlusFill, Trash, TrophyFill } from "react-bootstrap-icons";
import { useTranslation } from 'react-i18next';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

function GameSessionEditor(props) {
    const { t } = useTranslation('messages');

    const [note, setNote] = useState("");
    const [playedAt, setPlayedAt] = useState(null);
    const [durationMinutes, setDurationMinutes] = useState("");
    const [players, setPlayers] = useState([]);
    const [newPlayerName, setNewPlayerName] = useState("");

    useEffect(() => {
        if (props.session) {
            setNote(props.session.note || "");
            setPlayedAt(props.session.playedAt ? new Date(props.session.playedAt) : null);
            setDurationMinutes(props.session.durationMinutes || "");
            setPlayers(props.session.players ? [...props.session.players] : []);
        } else {
            setNote("");
            setPlayedAt(null);
            setDurationMinutes("");
            setPlayers([]);
        }
        setNewPlayerName("");
    }, [props.session]);

    if (!props.show) {
        return <></>;
    }

    const isEdit = props.session && props.session.gameSessionId;

    const onAddPlayer = () => {
        if (newPlayerName.trim()) {
            setPlayers([...players, { name: newPlayerName.trim(), winner: false }]);
            setNewPlayerName("");
        }
    };

    const onRemovePlayer = (index) => {
        setPlayers(players.filter((_, i) => i !== index));
    };

    const onToggleWinner = (index) => {
        const updated = [...players];
        updated[index].winner = !updated[index].winner;
        setPlayers(updated);
    };

    return (
        <Offcanvas show={props.show} onHide={props.onClose} placement="bottom">
            <Offcanvas.Header closeButton data-bs-theme="dark"
                style={{ backgroundColor: '#2d1b4e', color: '#fff' }}>
                <Offcanvas.Title style={{ color: '#fff' }}>
                    {isEdit
                        ? t('game_sessions.edit_title')
                        : t('game_sessions.create_title')}
                    {props.gameName ? ` - ${props.gameName}` : ""}
                </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body style={{ backgroundColor: '#fdf4ff' }}>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('game_sessions.note_label')}</Form.Label>
                        <Form.Control
                            type="text"
                            value={note}
                            placeholder={t('game_sessions.note_placeholder')}
                            onChange={e => setNote(e.target.value)}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('game_sessions.played_at_label')}</Form.Label>
                        <div>
                            <DatePicker
                                selected={playedAt}
                                onChange={date => setPlayedAt(date)}
                                showTimeSelect
                                timeIntervals={15}
                                dateFormat="dd/MM/yyyy HH:mm"
                                timeFormat="HH:mm"
                                placeholderText={t('game_sessions.played_at_placeholder')}
                                customInput={<Form.Control />}
                                wrapperClassName="w-100"
                            />
                        </div>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('game_sessions.duration_label')}</Form.Label>
                        <Form.Control
                            type="number"
                            value={durationMinutes}
                            placeholder={t('game_sessions.duration_placeholder')}
                            onChange={e => setDurationMinutes(e.target.value)}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('game_sessions.players_label')}</Form.Label>
                        <div className="mb-2">
                            {players.map((player, index) => (
                                <Row key={index} className="mb-2 align-items-center g-2">
                                    <Col className="fw-semibold small">{player.name}</Col>
                                    <Col xs="auto">
                                        <Button
                                            size="sm"
                                            variant={player.winner ? "warning" : "outline-secondary"}
                                            onClick={() => onToggleWinner(index)}
                                            title={t('game_sessions.winner_checkbox')}>
                                            <TrophyFill size={13} />
                                        </Button>
                                    </Col>
                                    <Col xs="auto">
                                        <Button
                                            size="sm"
                                            variant="outline-danger"
                                            onClick={() => onRemovePlayer(index)}
                                            title={t('game_sessions.remove_player_button')}>
                                            <Trash size={13} />
                                        </Button>
                                    </Col>
                                </Row>
                            ))}
                        </div>
                        <InputGroup className="mb-3">
                            <Form.Control
                                type="text"
                                value={newPlayerName}
                                placeholder={t('game_sessions.new_player_placeholder')}
                                onChange={e => setNewPlayerName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        onAddPlayer();
                                    }
                                }}
                            />
                            <Button variant="outline-secondary" onClick={onAddPlayer}>
                                <PersonPlusFill size={16} />
                            </Button>
                        </InputGroup>
                    </Form.Group>
                    <Button
                        className="fw-semibold"
                        style={{ backgroundColor: '#6d28d9', borderColor: '#6d28d9', color: '#fff' }}
                        type="submit"
                        onClick={(e) => onSubmit(e, props, note, playedAt, durationMinutes, players)}>
                        {t('game_sessions.save_button')}
                    </Button>
                </Form>
            </Offcanvas.Body>
        </Offcanvas>
    );
}

function onSubmit(e, props, note, playedAt, durationMinutes, players) {
    e.preventDefault();

    const playedAtISO = playedAt ? playedAt.toISOString() : null;

    const isEdit = props.session && props.session.gameSessionId;

    if (isEdit) {
        props.onSave({
            gameSessionId: props.session.gameSessionId,
            note,
            playedAt: playedAtISO,
            durationMinutes: durationMinutes ? parseInt(durationMinutes) : 0,
            players
        });
    } else {
        props.onSave({
            externalId: props.externalId,
            gameType: props.gameType,
            note,
            playedAt: playedAtISO,
            durationMinutes: durationMinutes ? parseInt(durationMinutes) : 0,
            players
        });
    }

    props.onClose();
}

export default GameSessionEditor;
